import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const chapters = [
  {
    num: "01",
    title: "We remove reviews. We don't invent them.",
    body: "Every removal we file follows Google's own policy playbook — off-topic, spam, harassment, ex-employees, competitor sabotage, conflicts of interest. Nothing in a grey area. Nothing that risks your profile.",
  },
  {
    num: "02",
    title: "You pay for outcomes, not for effort.",
    body: "$195 is invoiced only after Google confirms the removal — verifiable in your Google Business dashboard. If a review can't be legitimately removed, you owe nothing.",
  },
  {
    num: "03",
    title: "Reputation is trust, compounded.",
    body: "One fake 1-star review costs a small business an estimated 22 customers before the next legitimate one arrives. Our job is to give those customers the honest signal you've already earned.",
  },
  {
    num: "04",
    title: "We work in the open.",
    body: "Every filing, every escalation, every reply from Google — all logged in your client dashboard. Nothing happens off the record.",
  },
];

/**
 * Numbered manifesto section — editorial column format with subtle scroll
 * parallax on the numeric numerals. Replaces the older "Ethics" callout.
 */
export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yBg = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      ref={ref}
      className="relative py-28 lg:py-40 overflow-hidden"
    >
      {/* Parallax word behind the columns */}
      <motion.div
        aria-hidden
        style={{ y: yBg }}
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
      >
        <span
          className="font-display font-black italic uppercase tracking-tighter text-[26vw] leading-none text-transparent"
          style={{ WebkitTextStroke: "1px rgba(212,175,55,0.06)" }}
        >
          Manifesto
        </span>
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.24em] text-[#D4AF37] font-semibold">
              Manifesto · Four beliefs
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold mt-4 leading-[0.95] tracking-tight">
              How we work.<br />
              <span className="text-white/40">And why the line matters.</span>
            </h2>
          </div>
          <div className="max-w-xs text-sm text-white/50 leading-relaxed">
            Most "reputation firms" cross a line that eventually costs you your
            Google presence. We built Go Higher to stay on the right side of it
            — permanently.
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-x-12 gap-y-16">
          {chapters.map((c, i) => (
            <motion.article
              key={c.num}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="flex items-baseline gap-6">
                <div className="font-display font-black text-[#D4AF37] text-6xl md:text-7xl tabular-nums leading-none">
                  {c.num}
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-white/30 via-white/10 to-transparent mb-3" />
              </div>
              <h3 className="mt-5 font-display font-bold text-2xl md:text-3xl leading-tight tracking-tight max-w-md">
                {c.title}
              </h3>
              <p className="mt-4 text-white/55 text-base leading-relaxed max-w-md">
                {c.body}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
